import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookingContext } from '@contexts/BookingContext';
import { Button } from '@/components/ui/button';

type WizardNavigationProps = {
  language: string;
};

const WizardNavigation: FC<WizardNavigationProps> = ({ language }) => {
  const { t } = useTranslation();
  const {
    currentStep,
    canGoBack,
    goBack,
    goForward,
    restart,
    bookingData,
    validateCurrentStep
  } = useBookingContext();

  const handleNextClick = async () => {
    if (currentStep === 'patientInformation') {
      // Validate the patient information form via context
      const isValid = await validateCurrentStep();

      if (!isValid) {
        return; // Don't proceed if validation fails
      }

      // Proceed to confirmation step - verification code will be handled by EndpointVerification component
      goForward('patientConfirmation');
    }
  };

  const handleBackClick = () => {
    if (currentStep === 'appointmentSelection') {
      restart();
    } else {
      goBack();
    }
  };

  // Hide back button for screening form and appointment selection
  const showBackButton = canGoBack &&
    currentStep !== 'screeningForm' &&
    currentStep !== 'appointmentSelection';

  return (
    <div className="flex justify-center space-x-3 py-4">
      {showBackButton && (
        <Button
          className="bg-primary/20 min-w-24 sm:min-w-20"
          variant="outline"
          onClick={handleBackClick}
        >
          {t("booking.buttons.back")}
        </Button>
      )}

      {currentStep === 'appointmentSelection' && (
        <Button
          variant="outline"
          className="bg-primary/20 min-w-24 sm:min-w-20"
          onClick={restart}
        >
          {t("booking.buttons.restart")}
        </Button>
      )}

      {currentStep === 'patientInformation' && (
        <Button
          className="min-w-24 sm:min-w-20"
          onClick={handleNextClick}
        >
          {t("booking.buttons.next")}
        </Button>
      )}
    </div>
  );
};

export default WizardNavigation;
